import { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useFinance, usePreferences } from '../../../contexts';
import styles from './FluxoAcumulado.module.css';

interface TooltipProps {
    active?: boolean;
    payload?: Array<{ value: number }>;
    label?: string;
    formatCurrency: (value: number) => string;
    labelSaldo: string;
}

const CustomTooltip = ({ active, payload, label, formatCurrency, labelSaldo }: TooltipProps) => {
    if (active && payload?.length) {
        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>{label}</p>
                <p style={{ color: '#159243' }}>
                    {labelSaldo}:{' '}
                    {formatCurrency(payload[0].value)}
                </p>
            </div>
        );
    }
    return null;
};

export function FluxoAcumulado() {
    const { cashFlowData } = useFinance();
    const { formatCurrency, t } = usePreferences();

    const data = useMemo(() => {
        let acumulado = 0;
        return cashFlowData.map((item) => {
            acumulado += item.income - item.expense;
            return { month: item.month, acumulado };
        });
    }, [cashFlowData]);

    const formatYAxis = (value: number) => `${(value / 1000).toFixed(0)}k`;

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{t('chart.fluxoAcumulado')}</h3>
            <span className={styles.subtitle}>{t('chart.evolucaoResultado')}</span>

            <div className={styles.chartContainer}>
                {data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                            <linearGradient id="acumuladoGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#b3e6c7" stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                        />
                        <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} labelSaldo={t('chart.saldoAcumulado')} />} />
                        <Area
                            type="monotone"
                            dataKey="acumulado"
                            stroke="#159243"
                            strokeWidth={2}
                            fill="url(#acumuladoGradient)"
                            animationDuration={1500}
                        />
                    </AreaChart>
                </ResponsiveContainer>
                ) : (
                    <div className={styles.empty}>{t('common.semDados')}</div>
                )}
            </div>
        </div>
    );
}
